import { watch, h } from "vue";
import { hotModules } from "../../components/DevPanel/overrides/websocket.js";
const generateContainerId = (id) => {
  const rand = Math.random().toString(36).substring(4).replace(/[^a-zA-Z]+/g, "");
  return `${id}-${rand}`;
};
const msg = (m) => `[µ-frontend]: ${m}`;
const onWrapperMount = async ({
  hooks,
  mf,
  getData,
  watchers,
  router,
  providers,
  allowEnvironmentOverrides = false
}, watchHandles, containerRef, onUnmount, isLoaded, mountId) => {
  var _a, _b;
  try {
    (_a = hooks == null ? void 0 : hooks.beforeMount) == null ? void 0 : _a.call(hooks, mf.id);
    const context = await getData(mf.id);
    const runHotEnv = allowEnvironmentOverrides && hotModules.entries.value.has(mf.fml.npmName);
    const mount = await mf.fml.getMountFn(runHotEnv ? { env: "hot" } : void 0).catch((e) => {
      hooks == null ? void 0 : hooks.failure(msg(`error loading mount function: ${e}`), {
        component: mf.id
      });
    });
    if (!mount) {
      throw new Error(msg(`cannot load mount function for ${mf.id}`));
    }
    if (!containerRef.value) {
      console.debug(msg(`🚫 will not mount ${mf.id} (${mountId})`));
      return;
    }
    const analytics = providers.getAnalytics(mf.context.analytics);
    const logger = providers.getLogger(mf.context.logger);
    const syncParentRouter = async (route) => {
      const currentPath = router.currentRoute.value.fullPath;
      if (typeof route === "string" && currentPath === route || typeof route === "object" && currentPath === route.path) {
        return;
      }
      await router.push(route);
    };
    const mfInstance = mount(`#${mountId}`, {
      callbacks: {
        ...context.callbacks,
        syncParentRouter
      },
      data: context.data,
      providers: {
        ...context.providers,
        analytics,
        logger
      }
    });
    if (mfInstance.unmount) {
      onUnmount.value = mfInstance.unmount;
      if (watchers) {
        for (const watcher of watchers) {
          watchHandles.push(await watcher(context.data));
        }
      }
    } else {
      hooks.failure("[shell-utilities] no unmount function", {
        component: mf.id,
        npmName: mf.fml.npmName
      });
    }
    if (mfInstance.syncChildRouter) {
      watchHandles.push(
        watch(
          () => router.currentRoute.value,
          (to) => {
            mfInstance.syncChildRouter(to.fullPath);
          },
          {
            immediate: true
          }
        )
      );
    } else {
      hooks.failure("[shell-utilities] no syncChildRouter function", {
        component: mf.id,
        npmName: mf.fml.npmName
      });
    }
    if (mfInstance.onSharedDataChanged) {
      watchHandles.push(
        watch(
          () => context.data,
          (data) => {
            if (mfInstance.onSharedDataChanged) {
              mfInstance.onSharedDataChanged(data);
            }
          },
          { deep: true }
        )
      );
    } else {
      hooks.failure("[shell-utilities] no onSharedDataChanged function", {
        component: mf.id,
        npmName: mf.fml.npmName
      });
    }
    console.debug(msg(`📥 mounted ${mf.id} (${mountId})`));
    isLoaded.value = true;
    (_b = hooks == null ? void 0 : hooks.afterMount) == null ? void 0 : _b.call(hooks, mf.id);
  } catch (mountError) {
    hooks == null ? void 0 : hooks.failure(msg(`error mounting ${mf.id}`), {
      error: mountError
    });
    isLoaded.value = false;
  }
};
const onWrapperUnmount = async (mfId, watchHandles, containerId, onUnmount, hooks) => {
  if (onUnmount.value) {
    onUnmount.value();
  }
  if (watchHandles.length) {
    for (const unwatch of watchHandles) {
      unwatch();
    }
    watchHandles = [];
  }
  console.debug(msg(`🗑️ unmounted ${mfId} (${containerId})`));
};
const renderRoot = (isLoaded, containerId, mfId, containerRef) => h("div", [
  isLoaded.value === false ? h(
    "dsf-banner",
    {
      "data-test": "shell-mf-not-loaded",
      dsf_level: "urgent",
      class: "max-w-max"
    },
    `${mfId} - is not available`
  ) : h("div", { id: containerId, ref: containerRef })
]);
export {
  generateContainerId,
  msg,
  onWrapperMount,
  onWrapperUnmount,
  renderRoot
};
